import { useCallback, useRef, useState } from "react";

import {
  ConfirmDialog,
  type ConfirmRequest,
} from "../components/UI/ConfirmDialog";

/**
 * `await confirm({...})` — the shape `window.confirm` had, without its dialog.
 *
 * A promise-returning hook rather than a component each caller wires up itself,
 * because that is what keeps the call sites honest: the guard stays one line at
 * the top of the handler it protects, exactly where the old `if
 * (!window.confirm(...)) return;` was, instead of being spread across a state
 * flag, a pending-action ref and a separate callback that has to remember what
 * it was going to do.
 *
 *   const { confirm, confirmDialog } = useConfirm();
 *   …
 *   if (!(await confirm({ title, description }))) return;
 *   …
 *   return (<>{page}{confirmDialog}</>);
 */
export function useConfirm() {
  const [request, setRequest] = useState<ConfirmRequest | null>(null);
  // Held in a ref, not state: resolving is not a render, and the answer has to
  // reach the exact promise the caller is awaiting.
  const resolveRef = useRef<((answer: boolean) => void) | null>(null);

  const settle = useCallback((answer: boolean) => {
    setRequest(null);
    resolveRef.current?.(answer);
    resolveRef.current = null;
  }, []);

  const confirm = useCallback((next: ConfirmRequest) => {
    // A second question while one is open answers the first with "no" rather
    // than stranding its promise unresolved.
    resolveRef.current?.(false);
    setRequest(next);
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const confirmDialog = (
    <ConfirmDialog
      open={!!request}
      title={request?.title ?? ""}
      description={request?.description}
      confirmLabel={request?.confirmLabel}
      cancelLabel={request?.cancelLabel}
      tone={request?.tone}
      maxWidthClass={request?.maxWidthClass}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  );

  return { confirm, confirmDialog };
}

